'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { desEncryptContent } from '../../data/des-encrypt-content';
import { Copy, Key, Lock, LockOpen, FileInput, Eye, EyeOff, Loader2, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

declare global {
    interface Window {
        crypto: Crypto & {
            subtle: SubtleCrypto;
            getRandomValues: <T extends ArrayBufferView | null>(array: T) => T;
        };
    }
}

type Operation = 'encrypt' | 'decrypt';

// Convert ArrayBuffer or ArrayBufferView to base64
function arrayBufferToBase64(buffer: ArrayBuffer | ArrayBufferView): string {
    const bytes = buffer instanceof ArrayBuffer
        ? new Uint8Array(buffer)
        : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

// Convert base64 to ArrayBuffer
function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

export default function DesEncrypt() {
    const { toast } = useToast();
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [password, setPassword] = useState('');
    const [operation, setOperation] = useState<Operation>('encrypt');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [iv, setIv] = useState<Uint8Array | null>(null);

    // Generate a random IV (Initialization Vector)
    const generateIv = useCallback((): Uint8Array => {
        const iv = new Uint8Array(8); // DES uses 8-byte IV
        window.crypto.getRandomValues(iv);
        return iv;
    }, []);

    // Import key from password
    const importKey = useCallback(async (password: string): Promise<CryptoKey> => {
        const encoder = new TextEncoder();
        const keyData = encoder.encode(password);

        // Ensure key is 8 bytes (DES key size)
        let keyBytes = new Uint8Array(8);
        const keyDataBytes = new Uint8Array(keyData.buffer);
        for (let i = 0; i < 8; i++) {
            keyBytes[i] = keyDataBytes[i % keyDataBytes.length];
        }

        return window.crypto.subtle.importKey(
            'raw',
            keyBytes,
            { name: 'DES-CBC' },
            false,
            ['encrypt', 'decrypt']
        );
    }, []);

    // Encrypt text using DES-CBC
    const encrypt = useCallback(async (text: string, password: string): Promise<string> => {
        try {
            const iv = generateIv();
            const key = await importKey(password);
            const encoder = new TextEncoder();
            const data = encoder.encode(text);

            // Ensure data length is a multiple of 8 (DES block size)
            const blockSize = 8;
            const paddingLength = blockSize - (data.length % blockSize);
            const paddedData = new Uint8Array(data.length + paddingLength);
            paddedData.set(data);
            paddedData.fill(paddingLength, data.length); // PKCS#7 padding

            // Create a new Uint8Array with explicit type information
            const dataBuffer = new Uint8Array(paddedData.buffer, paddedData.byteOffset, paddedData.byteLength);

            const encryptedData = await window.crypto.subtle.encrypt(
                {
                    name: 'DES-CBC',
                    iv: new Uint8Array(iv)
                },
                key,
                dataBuffer
            );

            // Combine iv and encrypted data
            const result = {
                iv: arrayBufferToBase64(iv),
                data: arrayBufferToBase64(encryptedData),
            };

            return JSON.stringify(result);
        } catch (error) {
            console.error('Encryption error:', error);
            throw new Error('Encryption failed');
        }
    }, [generateIv, importKey]);

    // Decrypt text using DES-CBC
    const decrypt = useCallback(async (encryptedText: string, password: string): Promise<string> => {
        try {
            const { iv: ivBase64, data: dataBase64 } = JSON.parse(encryptedText);
            const ivBuffer = new Uint8Array(base64ToArrayBuffer(ivBase64));
            const dataBuffer = base64ToArrayBuffer(dataBase64);

            const key = await importKey(password);

            const decryptedData = await window.crypto.subtle.decrypt(
                {
                    name: 'DES-CBC',
                    iv: ivBuffer,
                },
                key,
                dataBuffer
            );

            // Remove PKCS#7 padding
            const decryptedBytes = new Uint8Array(decryptedData);
            const paddingLength = decryptedBytes[decryptedBytes.length - 1];
            const unpaddedData = decryptedBytes.slice(0, -paddingLength);

            return new TextDecoder().decode(unpaddedData);
        } catch (error) {
            console.error('Decryption error:', error);
            throw new Error('Decryption failed. Invalid password or data.');
        }
    }, [importKey]);

    // Handle encryption/decryption
    const processText = useCallback(async () => {
        if (!inputText.trim() || !password) {
            setOutputText('');
            return;
        }

        setIsLoading(true);
        try {
            let result;
            if (operation === 'encrypt') {
                result = await encrypt(inputText, password);
            } else {
                result = await decrypt(inputText, password);
            }
            setOutputText(result);
        } catch (error) {
            console.error('Error:', error);
            toast({
                title: 'Error',
                description: error instanceof Error ? error.message : 'An error occurred',
                variant: 'destructive',
            });
            setOutputText('');
        } finally {
            setIsLoading(false);
        }
    }, [inputText, outputText, password, operation, encrypt, decrypt, toast]);

    // Auto-process when input or operation changes
    useEffect(() => {
        const timer = setTimeout(processText, 500);
        return () => clearTimeout(timer);
    }, [inputText, password, operation, processText]);

    // Copy text to clipboard
    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
    };

    // Clear all fields
    const clearAll = () => {
        setInputText('');
        setOutputText('');
        setPassword('');
        setIv(null);
    };

    // Toggle between encrypt and decrypt
    const toggleOperation = (op: Operation) => {
        setOperation(op);
        // Swap input and output when toggling
        setInputText(outputText);
        setOutputText(inputText);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                <ToolPageIntro title="DES Encryption Tool">
                    Encrypt or decrypt text with the DES algorithm. Choose a key and mode (ECB/CBC). For legacy or learning use; prefer AES for new projects. No sign-up.
                </ToolPageIntro>

                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>DES Encryption/Decryption</CardTitle>
                        <CardDescription>
                            {operation === 'encrypt'
                                ? 'Enter text to encrypt using DES-CBC'
                                : 'Enter encrypted text to decrypt'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <Tabs
                                value={operation}
                                onValueChange={(value) => toggleOperation(value as Operation)}
                                className="mb-4"
                            >
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="encrypt">
                                        <Lock className="w-4 h-4 mr-2" />
                                        Encrypt
                                    </TabsTrigger>
                                    <TabsTrigger value="decrypt">
                                        <LockOpen className="w-4 h-4 mr-2" />
                                        Decrypt
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>

                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter encryption password"
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-4 h-4" />
                                        ) : (
                                            <Eye className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                    Note: For DES, the password will be truncated/padded to 8 bytes.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="input">
                                    {operation === 'encrypt' ? 'Text to Encrypt' : 'Encrypted Text'}
                                </Label>
                                <div className="relative">
                                    <Textarea
                                        id="input"
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        placeholder={
                                            operation === 'encrypt'
                                                ? 'Enter your text here...'
                                                : 'Paste your encrypted text here...'
                                        }
                                        className="min-h-[150px] font-mono text-sm"
                                    />
                                    {inputText && (
                                        <button
                                            type="button"
                                            className="absolute top-2 right-2 p-1 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
                                            onClick={() => setInputText('')}
                                            title="Clear"
                                        >
                                            <span className="sr-only">Clear</span>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                                <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end space-x-2">
                                <Button
                                    variant="outline"
                                    onClick={clearAll}
                                    disabled={!inputText && !outputText && !password}
                                >
                                    Clear All
                                </Button>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="output">
                                        {operation === 'encrypt' ? 'Encrypted Result' : 'Decrypted Text'}
                                    </Label>
                                    {outputText && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => copyToClipboard(outputText)}
                                            className="text-xs h-7"
                                        >
                                            <Copy className="w-3 h-3 mr-1" />
                                            Copy to Clipboard
                                        </Button>
                                    )}
                                </div>
                                <div className="relative">
                                    <Textarea
                                        id="output"
                                        value={outputText}
                                        readOnly
                                        placeholder={
                                            operation === 'encrypt'
                                                ? 'Your encrypted text will appear here...'
                                                : 'Your decrypted text will appear here...'
                                        }
                                        className="min-h-[150px] font-mono text-sm bg-gray-50 dark:bg-gray-900"
                                    />
                                    {isLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-900/50">
                                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-muted/50">
                    <CardHeader>
                        <CardTitle className="text-lg">About DES Encryption</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="prose dark:prose-invert text-sm">
                            <p>
                                <strong>DES (Data Encryption Standard)</strong> is a symmetric-key block cipher that uses a 56-bit key.
                                This tool uses DES in CBC (Cipher Block Chaining) mode with PKCS#7 padding.
                            </p>
                            <p className="mt-2">
                                <strong>Note:</strong> DES is considered cryptographically weak by modern standards due to its short key length.
                                For better security, consider using AES encryption instead.
                            </p>
                            <ul className="mt-2 space-y-1 list-disc pl-5">
                                <li>Password is hashed to generate the encryption key</li>
                                <li>Uses a random IV (Initialization Vector) for each encryption</li>
                                <li>Output includes the IV and encrypted data in base64 format</li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* SEO CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={desEncryptContent.title}
                    description={desEncryptContent.description}
                    content={desEncryptContent.content}
                    faqs={desEncryptContent.faqs}
                    relatedTools={desEncryptContent.relatedTools}
                />
            </div>
        </div>
    );
}
